import React, { useRef, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { BrandTheme, BrandPaletteEntry, BrandThemeHistoryEntry } from '../../types/gotypes'
import type { WhoToAsk } from '../../utils/permissionReason'
import BrandThemeListEditor, { BrandThemeListEditorHandle } from './BrandThemeListEditor'

export interface ThemeManagerDialogProps {
  open: boolean
  onClose: () => void
  themes: BrandTheme[]
  loading?: boolean
  onSaveTheme: (id: string, name: string, palette: Record<string, BrandPaletteEntry>) => Promise<void>
  onCreateTheme?: (name: string, palette: Record<string, BrandPaletteEntry>) => Promise<void>
  onDeleteTheme: (id: string) => Promise<void>
  /** Three-tier ownership (#1266): see BrandThemeListEditor. */
  currentUserEmail?: string
  canPromote?: boolean
  onPromoteTheme?: (id: string) => Promise<void>
  onDemoteTheme?: (id: string) => Promise<void>
  onLoadThemeHistory?: (id: string) => Promise<BrandThemeHistoryEntry[]>
  /** When provided, each row gets an "Open" action that hands the theme back
   *  to the caller (e.g. to apply it as a page's theme, #1182). */
  onOpenTheme?: (theme: BrandTheme) => void
  /** Who to ask about a theme the user cannot change (useWhoToAsk). */
  whoToAsk?: WhoToAsk
}

/**
 * Standalone saved-theme manager (#973 item 4): lists a customer's saved brand
 * themes and allows open / edit / delete (plus create). Purely prop-driven —
 * pages wire data via the existing `useBrandThemes(customer)` hook
 * (frontend/src/hooks/useBrandThemes.ts) per the page → hook → component rule,
 * so it can mount anywhere: today inside the Page brand colors panel (#1182)
 * and the DataViz homepage's "Edit Themes".
 *
 * The list-edit body (create form, per-theme row, delete confirmation) is
 * shared with the admin BrandThemesSection via BrandThemeListEditor (#1066
 * item 2) — this component owns only the Dialog chrome.
 */
const ThemeManagerDialog: React.FC<ThemeManagerDialogProps> = ({
  open,
  onClose,
  themes,
  loading = false,
  onSaveTheme,
  onCreateTheme,
  onDeleteTheme,
  currentUserEmail,
  canPromote,
  onPromoteTheme,
  onDemoteTheme,
  onLoadThemeHistory,
  onOpenTheme,
  whoToAsk,
}) => {
  const editorRef = useRef<BrandThemeListEditorHandle>(null)
  const [isCreating, setIsCreating] = useState(false)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { minHeight: '70vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box component="span" sx={{ flex: 1 }}>Saved Themes</Box>
        {onCreateTheme && (
          <Button
            size="small"
            variant="outlined"
            color="info"
            startIcon={<AddIcon />}
            onClick={() => editorRef.current?.startCreate()}
            disabled={isCreating}
          >
            New Theme
          </Button>
        )}
      </DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        <BrandThemeListEditor
          ref={editorRef}
          themes={themes}
          loading={loading}
          onSave={onSaveTheme}
          onCreate={onCreateTheme}
          onDelete={onDeleteTheme}
          currentUserEmail={currentUserEmail}
          canPromote={canPromote}
          onPromote={onPromoteTheme}
          onDemote={onDemoteTheme}
          onLoadHistory={onLoadThemeHistory}
          onOpenTheme={onOpenTheme}
          whoToAsk={whoToAsk}
          onCreatingChange={setIsCreating}
          description="Company-level themes, your own, and your colleagues'. Edit your own; duplicate a colleague's to make it yours; Managers and Realm users promote a theme to company-level with the star. Apply one to a page from the palette icon on its page tab."
          emptyMessage="No saved themes yet."
          confirmDelete
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="info">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ThemeManagerDialog
